"use client";
import { useGetCurrentUser, useLogin } from "@/hooks/auth-hook";
import { cn } from "@/lib/utils";
import { LoginProps } from "@/types/main-types";
import { AnimatePresence } from "framer-motion";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import ErrorHandling from "../layout/ErrorHandling";
import AlreadyLogin from "./AlreadyLogin";
import AuthHeader from "./AuthHeader";
import AuthHero from "./AuthHero";
import TernsAuth from "./TernsAuth";

export default function Login() {
  const t = useTranslations("Auth");
  const locale = useLocale();
  const isRtl = locale === "ar";
  const navigate = useRouter();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState<{
    type: "error" | "success" | "";
    messages: string;
  }>({ type: "", messages: "" });

  const { mutate, isPending } = useLogin();
  const { data } = useGetCurrentUser();
  const currentLoggedUser = data?.user || null;

  const {
    register,
    handleSubmit,
    formState: { errors },
  }: any = useForm({ mode: "all" });

  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = (data: LoginProps) => {
    setErrorMessage({ messages: "", type: "" });
    mutate(data, {
      onSuccess: () => {
        navigate.push("/");
      },
      onError: (err: any) => {
        setErrorMessage({
          messages: err?.response?.data?.message || "Login failed.",
          type: "error",
        });
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8FAFC] overflow-hidden">
      {/* Left Side: Image & Branding */}
      <AuthHero />

      <div className="flex-1 flex flex-col relative bg-white">
        <AuthHeader />

        <div className="flex-1 flex flex-col justify-center px-6 md:px-20 lg:px-24">
          <div className="w-full max-w-md mx-auto">
            {/* Header Text */}
            <div className="mb-10">
              <h2 className="text-4xl font-black text-slate-900 mb-2">
                {t("login")}
              </h2>
              <p className="text-slate-500 font-medium">
                {isRtl
                  ? "مرحباً بك مجدداً، سجل دخولك للمتابعة."
                  : "Welcome back, please login to continue."}
              </p>
            </div>

            {errorMessage?.messages && (
              <div className="mb-6">
                <ErrorHandling
                  messages={errorMessage.messages}
                  type={errorMessage.type}
                />
              </div>
            )}

            {currentLoggedUser ? (
              <AlreadyLogin />
            ) : (
              <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
                {/* Email Input */}
                <div className="group space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-tighter px-1">
                    {t("email")}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...register("email", { required: true })}
                      className={cn(
                        "w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-4 outline-none transition-all font-bold text-slate-900 focus:bg-white focus:border-main",
                        errors.email && "border-red-400 focus:border-red-400",
                      )}
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div className="group space-y-2">
                  <div className="flex justify-between items-center px-1">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-tighter">
                      {t("password")}
                    </label>
                    <Link
                      href="/auth/reset-password"
                      className="text-xs font-black text-main hover:underline"
                    >
                      {t("forgetPassword")}
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password", { required: true })}
                      className={cn(
                        "w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-4 outline-none transition-all font-bold text-slate-900 focus:bg-white focus:border-main",
                        errors.password &&
                          "border-red-400 focus:border-red-400",
                      )}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute ltr:right-4 rtl:left-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400 hover:text-main transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="flex items-center gap-3 pt-2 text-start">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-5 h-5 rounded-md border-slate-300 text-main focus:ring-main/50 transition-all cursor-pointer accent-main"
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm font-bold text-slate-500 cursor-pointer select-none"
                  >
                    {isRtl
                      ? "لقد قرأت وأوافق على "
                      : "I have read and agree to the "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsTermsOpen(true);
                      }}
                      className="text-slate-900 underline hover:text-main underline-offset-4 transition-colors cursor-pointer"
                    >
                      {isRtl ? "الشروط والأحكام" : "Terms and Conditions"}
                    </button>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  disabled={isPending || !termsAccepted}
                  type="submit"
                  className="w-full bg-[#1A1A1A] text-white py-5 rounded-2xl font-black text-lg hover:bg-main transition-all shadow-2xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group cursor-pointer"
                >
                  {isPending ? (
                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{t("login")}</span>
                      <ArrowLeft
                        className={cn(
                          "w-5 h-5 transition-transform",
                          isRtl
                            ? "group-hover:-translate-x-1"
                            : "rotate-180 group-hover:translate-x-1",
                        )}
                      />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Terms & Conditions Modal */}
            <AnimatePresence>
              {isTermsOpen && (
                <TernsAuth
                  isRtl={isRtl}
                  setIsTermsOpen={setIsTermsOpen}
                  setTermsAccepted={setTermsAccepted}
                />
              )}
            </AnimatePresence>

            {!currentLoggedUser && (
              <div className="mt-12 text-center">
                <p className="text-slate-500 font-bold">
                  {t("don'tHaveAccount")}{" "}
                  <Link
                    href="/auth/signup"
                    className="text-main hover:underline decoration-2 underline-offset-4"
                  >
                    {t("signUp")}
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
